import { useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { toast } from "sonner";
import { Bold, Italic, Heading2, Heading3, Quote, List, ListOrdered, Link2, Minus, ImageIcon, Images, RectangleHorizontal, Undo2, Redo2, Pilcrow } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { uploadFiles } from "./ImageUploader";

const withClass = (ext) => ext.extend({ addAttributes() { return { ...this.parent?.(), class: { default: null } }; } });
const LinkX = withClass(Link).configure({ openOnClick: false, HTMLAttributes: { rel: null, target: null } });
const ImageX = withClass(Image).configure({ inline: true });

const Tb = ({ on, active, title, children, testid }) => (
  <button type="button" onMouseDown={(e) => { e.preventDefault(); on(); }} title={title} aria-label={title} data-testid={testid}
    className={`w-8 h-8 inline-flex items-center justify-center rounded hover:bg-slate-100 ${active ? "bg-slate-200 text-slate-900" : "text-slate-600"}`}>{children}</button>
);

export const RichEditor = ({ value, onChange, inline, testid = "rich-editor" }) => {
  const file = useRef(null);
  const mode = useRef("single");
  const editor = useEditor({
    extensions: [StarterKit.configure(inline ? { heading: false, blockquote: false, bulletList: false, orderedList: false, horizontalRule: false, codeBlock: false } : { codeBlock: false }), LinkX, ImageX],
    content: inline && value && !value.startsWith("<") ? `<p>${value}</p>` : value || "",
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? "" : e.getHTML()),
    editorProps: { attributes: { class: `rt rt-admin focus:outline-none ${inline ? "min-h-[40px]" : "min-h-[140px]"} px-3 py-2`, "data-testid": `${testid}-content` } },
  });
  if (!editor) return null;
  const c = () => editor.chain().focus();
  const link = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Indirizzo del collegamento (es. /contatti oppure https://...). Lascia vuoto per rimuoverlo.", prev);
    if (url === null) return;
    url ? c().extendMarkRange("link").setLink({ href: url }).run() : c().unsetLink().run();
  };
  const button = () => {
    const text = window.prompt("Testo del pulsante", "Richiedi un preventivo");
    if (!text) return;
    const url = window.prompt("Collegamento del pulsante", "/contatti");
    if (url) c().insertContent(`<p><a href="${url.replace(/"/g, "")}" class="btn-ink">${text.replace(/</g, "")}</a></p>`).run();
  };
  const pick = (m) => { mode.current = m; file.current.multiple = m === "gallery"; file.current.click(); };
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const { urls, failed } = await uploadFiles(files);
    const cls = mode.current === "gallery" ? ' class="rt-gal"' : "";
    if (urls.length) c().insertContent(`<p>${urls.map((u) => `<img src="${imgUrl(u)}" alt=""${cls}>`).join("")}</p>`).run();
    if (failed.length) toast.error(`${failed.length} foto non caricate`, { description: failed.slice(0, 3).join("\n") });
  };
  return (
    <div className="border border-slate-200 rounded-md bg-white" data-testid={testid}>
      <div className="flex flex-wrap gap-0.5 border-b border-slate-100 p-1">
        <Tb on={() => c().toggleBold().run()} active={editor.isActive("bold")} title="Grassetto" testid={`${testid}-bold`}><Bold size={15} /></Tb>
        <Tb on={() => c().toggleItalic().run()} active={editor.isActive("italic")} title="Corsivo" testid={`${testid}-italic`}><Italic size={15} /></Tb>
        <Tb on={link} active={editor.isActive("link")} title="Collegamento" testid={`${testid}-link`}><Link2 size={15} /></Tb>
        {!inline && <>
          <Tb on={() => c().setParagraph().run()} active={editor.isActive("paragraph")} title="Testo normale"><Pilcrow size={15} /></Tb>
          <Tb on={() => c().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Titolo" testid={`${testid}-h2`}><Heading2 size={15} /></Tb>
          <Tb on={() => c().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Sottotitolo"><Heading3 size={15} /></Tb>
          <Tb on={() => c().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Citazione" testid={`${testid}-quote`}><Quote size={15} /></Tb>
          <Tb on={() => c().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Elenco puntato" testid={`${testid}-ul`}><List size={15} /></Tb>
          <Tb on={() => c().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Elenco numerato"><ListOrdered size={15} /></Tb>
          <Tb on={() => c().setHorizontalRule().run()} title="Separatore"><Minus size={15} /></Tb>
          <Tb on={() => pick("single")} title="Immagine"><ImageIcon size={15} /></Tb>
          <Tb on={() => pick("gallery")} title="Galleria (più foto affiancate)"><Images size={15} /></Tb>
          <Tb on={button} title="Pulsante" testid={`${testid}-button`}><RectangleHorizontal size={15} /></Tb>
          <Tb on={() => c().undo().run()} title="Annulla"><Undo2 size={15} /></Tb>
          <Tb on={() => c().redo().run()} title="Ripeti"><Redo2 size={15} /></Tb>
        </>}
      </div>
      <EditorContent editor={editor} />
      <input ref={file} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={onFiles} />
    </div>
  );
};
